/**
 * Market-value formatting: prices, quotes, market caps, volumes, supply —
 * values that are the same for every user and are never privacy-redacted.
 *
 * USD-only chart/axis surface. Everything user-visible goes through
 * `useMarketValueFormatter`; these named functions remain for chart-bound
 * callbacks whose coordinate space must stay in USD.
 */
export {
  formatCompactCurrency as formatMarketCompactCurrency,
  formatCurrency as formatMarketCurrency,
  formatQuantity as formatMarketQuantity,
  formatSubscriptCurrencyParts as formatMarketSubscriptCurrencyParts,
} from "~/lib/display-format";
export { formatUsd as formatMarketUsd } from "~/lib/formatters";

import type { FormatCurrencyOptions, NumericInput } from "~/lib/display-format";
import { formatSubscriptCurrencyParts } from "~/lib/display-format";
import { formatUsdBalance } from "~/lib/formatters";

const MARKET_PRICE_SUBSCRIPT_THRESHOLD_USD = 0.01;

/**
 * Per-unit asset price display. Market data — never redacted.
 *
 * Balances epsilon-round dust to "$0" (sub-penny noise distracts), but a
 * price can genuinely be tiny — a micro-cap price must render, so below a
 * penny this falls through to CoinGecko-style subscript notation
 * ("$0.0₁₁131") instead of the balance-style zero clamp.
 */
export function formatMarketPrice(
  value: NumericInput,
  options?: Pick<FormatCurrencyOptions, "currency" | "fallback">,
): string {
  const numeric =
    typeof value === "bigint"
      ? Number(value)
      : typeof value === "string"
        ? Number.parseFloat(value)
        : value;

  if (
    typeof numeric === "number" &&
    Number.isFinite(numeric) &&
    numeric !== 0 &&
    Math.abs(numeric) < MARKET_PRICE_SUBSCRIPT_THRESHOLD_USD
  ) {
    return formatSubscriptCurrencyParts(value, options).text;
  }

  return formatUsdBalance(
    typeof value === "bigint" ? Number(value) : value,
    options,
  );
}
